import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import ArchetypeBadge from '../components/ArchetypeBadge';
import Icon from '../components/Icon';

const PLAYOFF_SPOTS = 4;

function PctBar({ value, type }) {
  const cls = value >= 70 ? 'high' : value >= 30 ? 'mid' : 'low';
  return (
    <div className="pct-bar-wrap">
      <div className="pct-bar">
        <div className={`pct-fill ${type}`} style={{ width: `${Math.min(value, 100)}%` }} />
      </div>
      <span className={`pct-num ${cls}`}>{value}%</span>
    </div>
  );
}

function Avatar({ avatar, name, size = 32 }) {
  if (avatar) {
    return (
      <img
        className="avatar"
        src={`https://sleepercdn.com/avatars/thumbs/${avatar}`}
        alt={name}
        style={{ width: size, height: size }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="avatar-fallback" style={{ width: size, height: size }}>
      {name?.slice(0, 2).toUpperCase() || '??'}
    </div>
  );
}

function GradePill({ grade }) {
  const colors = {
    'A+': { bg: 'rgba(0,230,118,0.12)', color: '#00e676', border: 'rgba(0,230,118,0.3)' },
    'A':  { bg: 'rgba(0,230,118,0.12)', color: '#00e676', border: 'rgba(0,230,118,0.3)' },
    'A-': { bg: 'rgba(0,230,118,0.10)', color: '#52d98a', border: 'rgba(82,217,138,0.3)' },
    'B+': { bg: 'rgba(var(--accent-rgb),0.12)', color: 'var(--accent)', border: 'rgba(var(--accent-rgb),0.3)' },
    'B':  { bg: 'rgba(var(--accent-rgb),0.12)', color: 'var(--accent)', border: 'rgba(var(--accent-rgb),0.3)' },
    'B-': { bg: 'rgba(var(--accent-rgb),0.10)', color: 'var(--accent)', border: 'rgba(var(--accent-rgb),0.3)' },
    'C+': { bg: 'rgba(255,211,42,0.12)', color: '#ffd32a', border: 'rgba(255,211,42,0.3)' },
    'C':  { bg: 'rgba(255,211,42,0.12)', color: '#ffd32a', border: 'rgba(255,211,42,0.3)' },
    'C-': { bg: 'rgba(255,211,42,0.10)', color: '#e8c244', border: 'rgba(232,194,68,0.3)' },
    'D':  { bg: 'rgba(var(--accent2-rgb),0.12)', color: 'var(--accent2)', border: 'rgba(var(--accent2-rgb),0.3)' },
    'F':  { bg: 'rgba(255,71,87,0.12)',  color: '#ff4757', border: 'rgba(255,71,87,0.3)' },
  };
  const style = colors[grade] || colors['C'];
  return (
    <div style={{
      background: style.bg, border: `1px solid ${style.border}`,
      borderRadius: 8, padding: '8px 18px', textAlign: 'center',
    }}>
      <div className="dashboard-stat-value" style={{ fontSize: '2.8rem', color: style.color }}>{grade}</div>
      <div className="dashboard-stat-label" style={{ marginTop: 2 }}>Roster</div>
    </div>
  );
}

function ActionItems({ api, rosterId }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!rosterId) return;
    fetch(`${api}/api/action-items/${rosterId}`)
      .then(r => r.json())
      .then(d => { setActions(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [api, rosterId]);

  if (loading || !actions.length) return null;

  const highPriority = actions.filter(a => a.priority === 'high');
  const visible = expanded ? actions : actions.slice(0, 3);

  return (
    <div style={{ marginTop: 14 }}>
      <div className="dashboard-eyebrow dashboard-action-heading">
        Action Items
        {highPriority.length > 0 && (
          <span style={{
            background: 'rgba(255,71,87,0.15)', color: 'var(--red)',
            fontSize: '0.58rem', padding: '1px 6px', borderRadius: 10,
            fontWeight: 700,
          }}>{highPriority.length} urgent</span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {visible.map((action, i) => {
          const borderColor = action.priority === 'high' ? 'rgba(255,71,87,0.3)'
            : action.priority === 'medium' ? 'rgba(255,211,42,0.25)'
            : 'rgba(255,255,255,0.1)';
          const bgColor = action.priority === 'high' ? 'rgba(255,71,87,0.06)'
            : action.priority === 'medium' ? 'rgba(255,211,42,0.05)'
            : 'rgba(255,255,255,0.03)';
          return (
            <div key={i} style={{
              background: bgColor, border: `1px solid ${borderColor}`,
              borderRadius: 8, padding: '10px 12px',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{action.icon}</span>
                <div style={{ flex: 1 }}>
                  <div className="dashboard-action-title">
                    {action.title}
                  </div>
                  <div className="dashboard-action-copy">
                    {action.action}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {actions.length > 3 && (
        <button
          className="dashboard-action-toggle"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? '▲ Show less' : `▼ Show ${actions.length - 3} more`}
        </button>
      )}
    </div>
  );
}

function MyTeamBanner({ api, rosterId, user }) {
  const [health, setHealth] = useState(null);
  const [standing, setStanding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!rosterId) return;
    const load = async () => {
      try {
        const [healthRes, standingsRes] = await Promise.all([
          fetch(`${api}/api/roster-health`),
          fetch(`${api}/api/standings`),
        ]);
        const healthData = await healthRes.json();
        const standingsData = await standingsRes.json();
        setHealth(healthData.find(h => h.roster_id === rosterId) || null);
        setStanding(standingsData.find(s => s.roster_id === rosterId) || null);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [api, rosterId]);

  if (!rosterId || loading) return null;
  if (!health && !standing) return null;

  const inPlayoff = standing && standing.rank <= PLAYOFF_SPOTS;
  const playoffPct = standing?.playoff_pct ?? 0;
  const champPct = standing?.championship_pct ?? 0;
  const emptySlots = health?.empty_slots ?? 0;
  const shortPositions = health?.short_positions ?? [];

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(var(--accent-rgb),0.08) 0%, rgba(var(--accent2-rgb),0.06) 100%)',
      border: '1px solid rgba(var(--accent-rgb),0.2)',
      borderRadius: 12, padding: '20px 24px', marginBottom: 24,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--accent), var(--accent2))' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Avatar avatar={user.avatar} name={user.display_name} size={36} />
        <div>
          <div className="dashboard-eyebrow dashboard-eyebrow--accent">Your Team</div>
          <div className="dashboard-hero-team-name">
            {user.team_name || user.display_name}
          </div>
        </div>
        {inPlayoff ? (
          <span style={{
            marginLeft: 'auto', background: 'rgba(0,230,118,0.12)', border: '1px solid rgba(0,230,118,0.3)',
            color: 'var(--green)', fontSize: '0.7rem', padding: '3px 10px', borderRadius: 20,
          }} className="dashboard-hero-status"><Icon emoji="🏆" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />In playoff position</span>
        ) : standing && (
          <span style={{
            marginLeft: 'auto', background: 'rgba(255,71,87,0.1)', border: '1px solid rgba(255,71,87,0.25)',
            color: 'var(--red)', fontSize: '0.7rem', padding: '3px 10px', borderRadius: 20,
          }} className="dashboard-hero-status">#{standing.rank} — Outside playoffs</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {standing && (
          <div style={statBox}>
            <div style={statVal}>{standing.wins}-{standing.losses}</div>
            <div style={statLbl}>Record</div>
          </div>
        )}
        {standing && (
          <div style={statBox}>
            <div style={statVal}>{standing.pf?.toFixed(1)}</div>
            <div style={statLbl}>Pts For</div>
          </div>
        )}
        {standing && (
          <div style={{ ...statBox, minWidth: 110 }}>
            <div style={{ ...statVal, color: playoffPct >= 70 ? 'var(--green)' : playoffPct >= 30 ? '#ffd32a' : 'var(--red)' }}>
              {playoffPct}%
            </div>
            <div style={statLbl}>Heuristic Playoff Odds</div>
            <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, marginTop: 6 }}>
              <div style={{ height: '100%', width: `${Math.min(playoffPct, 100)}%`, background: 'linear-gradient(90deg, var(--accent), var(--green))', borderRadius: 2 }} />
            </div>
          </div>
        )}
        {standing && (
          <div style={{ ...statBox, minWidth: 110 }}>
            <div style={{ ...statVal, color: champPct >= 20 ? '#ffd32a' : 'rgba(255,255,255,0.5)' }}>
              {champPct}%
            </div>
            <div style={statLbl}>Champ Odds</div>
            <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, marginTop: 6 }}>
              <div style={{ height: '100%', width: `${Math.min(champPct * 3, 100)}%`, background: 'linear-gradient(90deg, var(--accent), var(--accent2))', borderRadius: 2 }} />
            </div>
          </div>
        )}
        {health && <GradePill grade={health.grade} />}

        {health?.team_needs?.length > 0 && (
          <div style={{ ...statBox, flex: 1, minWidth: 180 }}>
            <div className="dashboard-eyebrow" style={{ marginBottom: 8 }}>
              Team Needs
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {health.team_needs.slice(0, 3).map((need, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    background: 'rgba(var(--accent2-rgb),0.15)', color: 'var(--accent2)',
                    fontSize: '0.65rem', fontWeight: 700, padding: '1px 6px',
                    borderRadius: 4, fontFamily: 'JetBrains Mono, monospace',
                    minWidth: 28, textAlign: 'center',
                  }}>{need.position}</span>
                  <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.3 }}>{need.reason}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {health?.injured_players?.length > 0 && (
          <div style={{ ...statBox, minWidth: 150 }}>
            <div className="dashboard-eyebrow" style={{ marginBottom: 8 }}>
              Injury Alert
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {health.injured_players.slice(0, 3).map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    background: 'rgba(255,71,87,0.15)', color: 'var(--red)',
                    fontSize: '0.6rem', fontWeight: 700, padding: '1px 5px',
                    borderRadius: 4, fontFamily: 'JetBrains Mono, monospace',
                  }}>{p.status}</span>
                  <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)' }}>{p.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {(emptySlots > 0 || shortPositions.length > 0) && (
        <div style={{
          marginTop: 14, padding: '10px 14px',
          background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.2)',
          borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
        }}>
          <Icon emoji="⚠️" size={18} />
          <div>
            {emptySlots > 0 && (
              <div style={{ fontSize: '0.78rem', color: 'var(--red)', fontWeight: 600, marginBottom: shortPositions.length > 0 ? 4 : 0 }}>
                You have {emptySlots} empty roster spot{emptySlots !== 1 ? 's' : ''} — your bench is not full.
              </div>
            )}
            {shortPositions.map((sp, i) => (
              <div key={i} style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
                {sp.position}: have {sp.have}, recommend at least {sp.need} — need {sp.short_by} more
              </div>
            ))}
          </div>
        </div>
      )}

      <ActionItems api={api} rosterId={rosterId} />
    </div>
  );
}

const statBox = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8, padding: '10px 14px', minWidth: 80,
};
const statVal = {
  fontFamily: 'var(--font-display)', fontSize: '1.6rem',
  color: 'var(--text)', lineHeight: 1, marginBottom: 2,
};
const statLbl = {
  fontSize: '0.62rem', color: 'var(--text3)', fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '1px',
  fontFamily: 'var(--font-body)',
};

export default function Standings({ api }) {
  const { currentUser } = useUser();
  const [data, setData] = useState([]);
  const [archetypes, setArchetypes] = useState({});   // {user_id: archetype_name}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [standR, archR] = await Promise.all([
          fetch(`${api}/api/standings`),
          fetch(`${api}/api/archetypes`).catch(() => null),
        ]);
        setData(await standR.json());
        setError(false);
        if (archR && archR.ok) {
          const archData = await archR.json();
          const map = {};
          (archData.archetypes || []).forEach(a => { if (a.user_id) map[a.user_id] = a.archetype; });
          setArchetypes(map);
        }
      } catch (e) { console.error(e); setError(true); }
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 900000);
    return () => clearInterval(interval);
  }, [api]);

  if (loading) return (
    <div className="loading"><div className="spinner" /><span>Loading standings...</span></div>
  );

  if (error) return (
    <div className="empty-state">
      <div style={{ marginBottom: 12 }}><Icon emoji="⚠️" size={28} /></div>
      <p>Could not load standings. Check your connection and refresh.</p>
    </div>
  );

  if (!data.length) return (
    <div className="empty-state">
      <div style={{ marginBottom: 12 }}><Icon emoji="⏳" size={28} /></div>
      <p>Standings are warming up — this takes about 30 seconds on first start.</p>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div className="page-title">League Standings</div>
        <div className="page-subtitle">Heuristic playoff odds via Monte Carlo simulation (10,000 runs)</div>
      </div>

      {currentUser && !currentUser.guest && currentUser.roster_id && (
        <MyTeamBanner api={api} rosterId={currentUser.roster_id} user={currentUser} />
      )}

      <div className="card">
        <div className="sim-info">
          <div className="sim-dot" />
          <span>Heuristic playoff odds: 10,000 simulated outcomes using current record and points-for strength; not player projections or the actual remaining schedule.</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="standings-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Team</th>
                <th>W-L</th>
                <th className="right">PF</th>
                <th className="right">PA</th>
                <th>Streak</th>
                <th>Playoff %</th>
                <th>Champ %</th>
              </tr>
            </thead>
            <tbody>
              {data.map((team) => {
                const isLast = team.rank === PLAYOFF_SPOTS;
                const inPlayoff = team.rank <= PLAYOFF_SPOTS;
                const isMe = currentUser && team.roster_id === currentUser.roster_id;
                return (
                  <React.Fragment key={team.roster_id}>
                    <tr
                      className={`team-row ${inPlayoff ? 'in-playoff' : ''} ${isLast ? 'playoff-line' : ''}`}
                      style={isMe ? { background: 'rgba(var(--accent-rgb),0.05)', outline: '1px solid rgba(var(--accent-rgb),0.15)' } : {}}
                    >
                      <td>
                        <span className={`rank-num ${team.rank <= 3 ? 'top' : ''}`}>{team.rank}</span>
                        {inPlayoff && <span className="playoff-marker"><Icon emoji="🏆" size={13} /></span>}
                      </td>
                      <td>
                        <div className="team-name-cell">
                          <Avatar avatar={team.avatar} name={team.display_name} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                            <span className="team-name">
                              {team.display_name}
                              {isMe && (
                                <span className="standings-you-badge">You</span>
                              )}
                            </span>
                            {archetypes[team.owner_id] && (
                              <ArchetypeBadge archetype={archetypes[team.owner_id]} size="sm" />
                            )}
                          </div>
                        </div>
                      </td>
                      <td><span className="record">{team.wins}-{team.losses}{team.ties > 0 ? `-${team.ties}` : ''}</span></td>
                      <td className="right"><span className="pts">{team.pf?.toFixed(1)}</span></td>
                      <td className="right"><span className="pts" style={{ color: 'var(--text3)' }}>{team.pa?.toFixed(1)}</span></td>
                      <td>
                        {team.streak?.count > 0 && (
                          <span className={`streak ${team.streak.type === 'W' ? 'win' : 'loss'}`}>
                            {team.streak.type}{team.streak.count}
                          </span>
                        )}
                      </td>
                      <td><PctBar value={team.playoff_pct ?? 0} type="playoff" /></td>
                      <td><PctBar value={team.championship_pct ?? 0} type="champ" /></td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 16, padding: '12px 0', borderTop: '1px solid var(--border)', display: 'flex', gap: 20 }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text3)' }}>
            <Icon emoji="🏆" size={13} style={{ color: 'var(--green)', verticalAlign: 'middle', marginRight: 4 }} /> = Playoff position (Top {PLAYOFF_SPOTS})
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text3)' }}>Dashed line = Playoff cutoff</span>
        </div>
      </div>
    </div>
  );
}
